import { useEffect, useState } from "react";

import { api, ApiError } from "../lib/api";
import { useLive } from "../lib/use-live";
import { useT } from "../context/locale";
import type { TranslationKey } from "../i18n";
import { useAuth } from "../context/auth";
import { useToast } from "../context/toast";
import {} from "../lib/format";
import { useRelative, useDateTime } from "../lib/use-relative";
import {
  ActionButton,
  ActionIcon,
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Modal,
  Panel,
  RowActions,
  Table,
  Td,
  Tr,
} from "../components/ui";
import { SelectField, TextField, Toggle } from "../components/ui/form";
import {
  FilterBar,
  PageBar,
  SortHeader,
  useFilters,
  useSort,
} from "../components/ui/filters";
import type { AdminProfile, AdminRole, Paged } from "../types/api";

/** Ordered least to most privileged. Labels are looked up where they render. */
const ROLE_VALUES: AdminRole[] = ["support", "admin", "superadmin"];

const ROLE_TONE: Record<AdminRole, "neutral" | "info" | "gold"> = {
  support: "neutral",
  admin: "info",
  superadmin: "gold",
};

export function Admins() {
  const dateTime = useDateTime();
  const t = useT();
  const relative = useRelative();
  const { admin: me } = useAuth();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const filters = useFilters({ q: "", role: "", isActive: "" }, () =>
    setPage(1),
  );
  const sort = useSort("createdAt", "desc", () => setPage(1));

  const admins = useLive<Paged<AdminProfile>>("admins", {
        page,
        perPage,
        sort: sort.sort,
        order: sort.order,
        q: filters.applied.q || undefined,
        role: filters.applied.role || undefined,
        isActive: filters.applied.isActive || undefined,
      });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminProfile | null>(null);
  const [deleting, setDeleting] = useState<AdminProfile | null>(null);

  return (
    <div className="space-y-5">
      <FilterBar
        loading={admins.loading}
        state={filters}
        filters={[
          { key: "q", label: t("common.username"), placeholder: t("players.filter.usernamePlaceholder") },
          {
            key: "role",
            label: t("common.role"),
            type: "select",
            options: [
              { value: "", label: t("admins.anyRole") },
              ...ROLE_VALUES.map((role) => ({
                value: role,
                label: t(`admins.role.${role}` as TranslationKey),
              })),
            ],
          },
          {
            key: "isActive",
            label: t("common.state"),
            type: "select",
            options: [
              { value: "", label: t("admins.anyState") },
              { value: "true", label: t("referrals.active") },
              { value: "false", label: t("referrals.disabled") },
            ],
          },
        ]}
      />

      <Panel
        eyebrow={t("nav.group.people")}
        title={t("nav.admins")}
        bodyClassName=""
        actions={
          <>
            <Button size="sm" onClick={admins.reload}>
              {t("action.refresh")}
            </Button>
            <Button size="sm" variant="gold" onClick={() => setCreating(true)}>
              {t("admins.new")}
            </Button>
          </>
        }
      >
        {admins.error ? (
          <div className="p-5">
            <ErrorNote message={admins.error} onRetry={admins.reload} />
          </div>
        ) : (
          <>
            <Table
              head={[
                <SortHeader key="u" field="username" state={sort}>
                  {t("common.username")}
                </SortHeader>,
                <SortHeader key="d" field="displayName" state={sort}>
                  {t("field.displayName")}
                </SortHeader>,
                <SortHeader key="r" field="role" state={sort}>
                  {t("common.role")}
                </SortHeader>,
                <SortHeader key="a" field="isActive" state={sort}>
                  {t("common.state")}
                </SortHeader>,
                <SortHeader key="l" field="lastLoginAt" state={sort}>
                  {t("col.lastSignIn")}
                </SortHeader>,
                <span key="a" className="block text-right">
                  {t("col.actions")}
                </span>,
              ]}
            >
              {admins.loading ? (
                <LoadingRows columns={6} />
              ) : admins.data && admins.data.items.length === 0 ? (
                <Tr>
                  <Td colSpan={6}>
                    <EmptyState title={t("admins.empty")} />
                  </Td>
                </Tr>
              ) : (
                (admins.data?.items ?? []).map((row) => {
                  const isSelf = row.id === me?.id;
                  return (
                    <Tr key={row.id}>
                      <Td>
                        <span className="font-semibold">{row.username}</span>
                        {isSelf ? (
                          <span className="text-dim ml-2 text-[11px]">you</span>
                        ) : null}
                      </Td>
                      <Td className="text-muted">{row.displayName}</Td>
                      <Td>
                        <Badge tone={ROLE_TONE[row.role]}>
                          {t(`admins.role.${row.role}` as TranslationKey)}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge tone={row.isActive ? "success" : "danger"}>
                          {row.isActive ? "active" : "disabled"}
                        </Badge>
                      </Td>
                      <Td
                        className="text-dim text-[12px]"
                        title={dateTime(row.lastLoginAt)}
                      >
                        {relative(row.lastLoginAt)}
                      </Td>
                      <Td>
                        <RowActions>
                          <ActionButton
                            icon={ActionIcon.edit}
                            onClick={() => setEditing(row)}
                          >
                            {t("action.edit")}
                          </ActionButton>
                          {/* Deleting yourself is the one refusal on this
                              screen, so it is shown rather than hidden — the
                              operator needs to see that the control exists and
                              why it is not theirs to press. */}
                          <ActionButton
                            icon={ActionIcon.delete}
                            tone="danger"
                            disabledWhy={isSelf ? t("admins.cannotDeleteSelf") : null}
                            onClick={() => setDeleting(row)}
                          >
                            {t("action.delete")}
                          </ActionButton>
                        </RowActions>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Table>

            {admins.data ? (
              <PageBar
                page={admins.data.page}
                pages={admins.data.pages}
                total={admins.data.total}
                perPage={perPage}
                onPage={setPage}
                onPerPage={(size) => {
                  setPerPage(size);
                  setPage(1);
                }}
              />
            ) : null}
          </>
        )}
      </Panel>

      <CreateAdminDialog
        open={creating}
        onClose={() => setCreating(false)}
        onDone={admins.reload}
      />
      <EditAdminDialog
        admin={editing}
        onClose={() => setEditing(null)}
        onDone={admins.reload}
        isSelf={editing?.id === me?.id}
      />
      <DeleteAdminDialog
        admin={deleting}
        onClose={() => setDeleting(null)}
        onDone={admins.reload}
      />
    </div>
  );
}

function CreateAdminDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<AdminRole>("support");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = /^[a-zA-Z0-9_]{3,32}$/.test(username) && password.length >= 8;

  function close() {
    setUsername("");
    setDisplayName("");
    setPassword("");
    setRole("support");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createAdmin({
        username: username.trim(),
        displayName: displayName.trim() || undefined,
        password,
        role,
      });
      toast.success(
        t("toast.adminCreated"),
        `${created.username} can sign in as ${created.role}.`,
      );
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.createAdmin"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      eyebrow={t("sessions.admin")}
      title={t("admins.new")}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="gold"
            onClick={submit}
            loading={busy}
            disabled={!valid}
          >
            {t("action.create")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label={t("common.username")}
          value={username}
          onChange={setUsername}
          error={
            username && !/^[a-zA-Z0-9_]{3,32}$/.test(username)
              ? "3–32 characters"
              : null
          }
          required
          autoFocus
        />
        <TextField
          label={t("field.displayName")}
          value={displayName}
          onChange={setDisplayName}
        />
        <TextField
          label={t("signIn.password")}
          value={password}
          onChange={setPassword}
          error={
            password && password.length < 8 ? t("field.minPassword") : null
          }
          required
        />
        <RoleField value={role} onChange={setRole} />
        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

function EditAdminDialog({
  admin,
  onClose,
  onDone,
  isSelf,
}: {
  admin: AdminProfile | null;
  onClose: () => void;
  onDone: () => void;
  isSelf?: boolean;
}) {
  const t = useT();
  const toast = useToast();
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<AdminRole>("support");
  const [isActive, setIsActive] = useState(true);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!admin) return;
    setDisplayName(admin.displayName ?? "");
    setRole(admin.role);
    setIsActive(admin.isActive);
    setPassword("");
    setError(null);
  }, [admin]);

  const valid = password === "" || password.length >= 8;

  async function submit() {
    if (!admin || !valid) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateAdmin(admin.id, {
        displayName: displayName.trim() || undefined,
        role: isSelf ? undefined : role,
        isActive: isSelf ? undefined : isActive,
        password: password || undefined,
      });
      toast.success(t("toast.adminUpdated"));
      onDone();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.updateAdmin"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(admin)}
      onClose={onClose}
      eyebrow={t("sessions.admin")}
      title={`Edit ${admin?.username ?? ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="gold"
            onClick={submit}
            loading={busy}
            disabled={!valid}
          >
            {t("action.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {isSelf ? (
          <p
            className="rounded-lg px-3 py-2 text-[12px]"
            style={{ background: "var(--info-bg)", color: "var(--info)" }}
          >
{t("admins.selfLocked")}
          </p>
        ) : null}

        <TextField
          label={t("field.displayName")}
          value={displayName}
          onChange={setDisplayName}
        />

        {!isSelf ? (
          <>
            <RoleField value={role} onChange={setRole} />
            {/* A state that takes effect on save, but reads as a setting. */}
            <Toggle
              label={t("admins.accountActive")}
              hint={t("admins.accountActiveHint")}
              checked={isActive}
              onChange={setIsActive}
            />
          </>
        ) : null}

        <TextField
          label={t("field.resetPassword")}
          value={password}
          onChange={setPassword}
          placeholder={t("common.none")}
          hint={t("field.keepPassword")}
          error={
            password && password.length < 8 ? t("field.minPassword") : null
          }
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

function DeleteAdminDialog({
  admin,
  onClose,
  onDone,
}: {
  admin: AdminProfile | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setTyped("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!admin || typed !== admin.username) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteAdmin(admin.id);
      toast.success(
        t("toast.adminDeleted"),
        `${admin.username} can no longer sign in.`,
      );
      onDone();
      close();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("error.deleteAdmin"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(admin)}
      onClose={close}
      eyebrow={t("sessions.admin")}
      title={`Delete ${admin?.username ?? ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            loading={busy}
            disabled={typed !== admin?.username}
          >
            {t("action.deletePermanently")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted text-[13px]">
{t("admins.deleteNote")}
        </p>
        <TextField
          label={`Type ${admin?.username ?? ""} to confirm`}
          value={typed}
          onChange={setTyped}
          required
          autoFocus
        />
        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

function RoleField({
  value,
  onChange,
}: {
  value: AdminRole;
  onChange: (role: AdminRole) => void;
}) {
  const t = useT();
  return (
    <SelectField
      label={t("common.role")}
      value={value}
      onChange={onChange}
      options={ROLE_VALUES.map((role) => ({
        value: role,
        label: t(`admins.role.${role}` as TranslationKey),
      }))}
      hint={t(`admins.roleScope.${value}` as TranslationKey)}
      required
    />
  );
}
